import { z } from 'zod';

/** Shared wire primitives every family builds on (SDD §13.1 conventions). */

/** Transport provenance: Phase 1 is simulated end-to-end; Phase 2 adds live bindings switched by env only. */
export const ModeSchema = z.enum(['simulated', 'live']);
export type Mode = z.infer<typeof ModeSchema>;

/** Response provenance marker carried by every read/write output that is an object. */
export const MetaSchema = z.object({ mode: ModeSchema, asOf: z.string() }).strict();
export type Meta = z.infer<typeof MetaSchema>;

/** Opaque ids are strings on the wire; the server owns their form. */
export const IdSchema = z.string().min(1).max(64);
export const IsoDateTimeSchema = z.string().min(20).max(40);
export const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const CurrencySchema = z.string().length(3);
/** Money is DECIMAL in storage (§16.2); on the wire it travels as a number in the account currency. */
export const MoneySchema = z.number().finite();

/** Client-generated idempotency key on every retryable mutation (§13.1 Idempotency). UUIDs in production; any
 *  8–128 char token is accepted so deterministic clients (sim, tests) can supply stable keys. */
export const IdempotencyKeySchema = z.string().min(8).max(128);

/** Cursor pagination on every growable collection — max page 200, stable ordering with id tiebreak. */
export const PAGE_MAX = 200;
export const CursorInputSchema = z
  .object({
    cursor: z.string().nullable().optional(),
    limit: z.number().int().min(1).max(PAGE_MAX).optional(),
  })
  .strict();
export type CursorInput = z.infer<typeof CursorInputSchema>;
export function pageOf<T extends z.ZodType>(item: T) {
  return z
    .object({
      meta: MetaSchema,
      items: z.array(item),
      nextCursor: z.string().nullable(),
    })
    .strict();
}
export type Page<T> = { meta: Meta; items: T[]; nextCursor: string | null };

/** Wraps a payload schema with the provenance marker. */
export function withMeta<S extends z.ZodRawShape>(shape: S) {
  return z.object({ meta: MetaSchema, ...shape }).strict();
}

/** The universal mutation acknowledgement. */
export const AckSchema = withMeta({ ok: z.literal(true), id: IdSchema.optional() });
export type Ack = z.infer<typeof AckSchema>;

/** Range vocabulary shared by dashboard/analytics/score filters (the reference's range chips). */
export const RangeSchema = z.enum(['day', 'week', 'month', 'year', 'all']);
export type Range = z.infer<typeof RangeSchema>;

/** Account scope filter: a specific account id or 'all'. */
export const AccountScopeSchema = z.string().min(1).default('all');
